import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { EditPanelButtonWithIcon } from 'js/editor/EditPanelButton/EditPanelButtonWithIcon';
import { ReactComponent as IconFilter } from 'imgs/svg-icons/IconFilter.svg';
import { Button } from '@sparkol/videoscribe-react-ui';
import LinkButton from 'js/shared/components/LinkButton';
import useSearchFilters from 'js/shared/hooks/useSearchFilters';
import { clearAllFilters } from 'js/actionCreators/imageSearchActions';
import useOnClickOutside from 'js/shared/hooks/useOnClickOutside';

import './ImageLibraryFilters.css';

import FilterOption from './FilterOption';
import FilterCountBadge from './FilterCountBadge';

const BUTTON_MENU_GAP = 4;

const ImageLibraryFilters = () => {
  const dispatch = useDispatch();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dialogStyle, setDialogStyle] = React.useState<React.CSSProperties>({});
  const [openFilter, setOpenFilter] = React.useState<string | null>(null);
  const { filters: filterOptions, selectedCount, loading } = useSearchFilters();
  const filterOptionsRef = useRef(filterOptions);

  let countLabel = '';
  if (selectedCount) {
    countLabel = selectedCount > 9 ? '9+' : selectedCount.toString();
  }

  const handleClick = () => {
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    const top = buttonRect ? buttonRect.top + buttonRect.height + BUTTON_MENU_GAP : 0;

    setDialogStyle({
      ...dialogStyle,
      top: `${top}px`,
      left: buttonRect ? `${buttonRect.left}px` : 0,
      maxHeight: `calc(100vh - ${top}px - 20px)`
    });
    dialogRef.current?.showModal();
  };

  const handleClearFilters = () => {
    dispatch(clearAllFilters());
  };

  const handleSearchButtonClick = () => {
    dialogRef.current?.close();
  };

  useEffect(() => {
    // On fresh search have the first filter open
    if (filterOptionsRef.current.length === 0 && filterOptions.length > 0) {
      setOpenFilter(filterOptions[0].title);
    }

    filterOptionsRef.current = filterOptions;
  }, [filterOptions]);

  useOnClickOutside(contentRef, () => {
    if (dialogRef.current?.open) {
      dialogRef.current.close();
    }
  });

  return (
    <div className="ImageLibraryFilters">
      {selectedCount > 0 && <LinkButton onClick={handleClearFilters}>Clear all filters</LinkButton>}
      <span className="ImageLibraryFilters__filterButton">
        <EditPanelButtonWithIcon
          ref={buttonRef}
          title="Filters"
          onClick={handleClick}
          Icon={<IconFilter />}
          className="EditPanelButton--largeType"
          disabled={filterOptions.length === 0}
        ></EditPanelButtonWithIcon>

        {!!countLabel && (
          <span className="ImageLibraryFilters__filterCount">
            <FilterCountBadge>{countLabel}</FilterCountBadge>
          </span>
        )}
      </span>

      {/* eslint-disable react/no-unknown-property */}
      {/* @ts-ignore closedby is HTML spec - types are incorrect */}
      <dialog ref={dialogRef} className="ImageLibraryFilters__dialog" style={dialogStyle}>
        <div className="ImageLibraryFilters__dialogContent" ref={contentRef}>
          <div className="ImageLibraryFilters__filters">
            {filterOptions.map(filterOption => {
              const handleClick = () => {
                setOpenFilter(openFilter === filterOption.title ? null : filterOption.title);
              };
              return (
                <FilterOption
                  id={filterOption.id}
                  onClick={handleClick}
                  title={filterOption.title}
                  options={filterOption.values}
                  open={filterOption.title === openFilter}
                  key={filterOption.title}
                />
              );
            })}
          </div>
          <div className="ImageLibraryFilters__controls">
            <LinkButton onClick={handleClearFilters} disabled={loading}>
              Clear all filters
            </LinkButton>
            <Button onClick={handleSearchButtonClick} variant="tertiary">
              Search
            </Button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default ImageLibraryFilters;
